import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import apiClient from "utils/apiClient";
import countries from "./countries";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

interface Series {
    name: string
    data: number[]
}

export type DashboardType = |'Account'|'Member';
export type DateType = |'hour'|'day'|'week'|'month'
export const platforms = [
    "total",
    "web",
    "winApp",
    "macApp",
    "desktop",
    "android",
    "ios",
    "mobile"
] as const

export type Platform = typeof platforms[number]

interface DashboardState {
    type: DashboardType
    startDate: string
    endDate: string
    dateType: DateType
    categories: string[]
    series: Series[]
    filterSeries: Series[]
    yaxisMax: number
    selectedPlatforms: Platform[]
    selectedCountries: string[]
    isLoading: boolean
}

type SearchProps = {
    startDate: string
    endDate: string
    groupType: DateType
    chargeType?: string
    page?: number
    perPage?: number
}

export const initInitialState = (type: DashboardType): DashboardState => {
    const endDate = dayjs().format('YYYY-MM-DD');
    const startDate = dayjs().add(-1, 'week').format('YYYY-MM-DD');

    return {
        type,
        startDate,
        endDate,
        dateType: 'day',
        categories: [],
        series: [],
        filterSeries: [],
        yaxisMax: 0,
        selectedPlatforms: ["total"],
        selectedCountries: ["ALL", "KR", "TW"],
        isLoading: true
    };
}

interface Records {
    dates: string[]
    series: Series[]
    max: number
}

const getUrl = (type: DashboardType) => {
    switch (type) {
        case "Account":
            return "/internal-api/v1/active/accounts";
        case "Member":
            return "/internal-api/v1/active/members";
    }
}

const getFormat = (dateType: DateType) => {
    switch (dateType) {
        case "hour":
            return 'YY-MM-DD HH'
        case "day":
        case "week":
        case "month":
            return "YYYY-MM-DD"
    }
}

const getActive = async (url, country, {startDate, endDate, groupType, chargeType = "all", page = 1, perPage = 1000}: SearchProps) => {
    const params = {
        startAt: dayjs(startDate).toISOString(),
        endAt: dayjs(endDate).toISOString(),
        groupType,
        country,
        chargeType,
        page,
        perPage
    }
    const response = await apiClient.get(url, { params });
    const dates: string[] = [];
    const categoryMap = new Map<string, number[]>();
    let max = 0;
    response.data.records.forEach((record) => {
        const date = dayjs.utc(record.date).tz().format(getFormat(groupType));
        dates.push(date);

        platforms.forEach(platform => {
            const category = record.country + "_" + platform;

            const value: number = record[platform];

            let values;
            if (categoryMap.has(category)) {
                values = categoryMap.get(category);
                values.push(value);
            } else {
                values = [value];
            }

            categoryMap.set(category, values);
            if (value > max) max = value;
        });
    });

    const series: Series[] = [];
    categoryMap.forEach((value, key) => {
        series.push({name: key, data: value});
    });

    return { dates, series, max };
}

const createGetActiveThunk = (type: DashboardType, url) => createAsyncThunk(
    'dashboard/getActive' + type,
    async (searchProps: SearchProps) => {
        const result: Records = { dates: [], series: [], max: 0 };
        let maxValue = 0;
        for (const country of countries) {
            const { dates, series, max } = await getActive(url, country.code, searchProps);

            if (result.dates.length === 0) result.dates = dates;
            else {
                result.dates = dates.filter(date => !result.dates.includes(date)).concat(result.dates);
            }
            if (result.series.length === 0) result.series = series;
            else result.series = series.concat(result.series);

            if (max > result.max) result.max = max;
        }

        return result;
    }
)

const filterSeries = (state: DashboardState) => {
    state.filterSeries = state.series.filter((series: Series) => {
        const seriesNameInfo = series.name.split('_');
        const country = seriesNameInfo[0];
        const platform = seriesNameInfo[1] as Platform;

        return state.selectedCountries.includes(country) &&
            state.selectedPlatforms.includes(platform);
    });
    state.isLoading = false;
}

// yaxis 의 최대값 구하기 위한 함수
const roundToMultiple = (num) => {
    const log10 = Math.ceil(Math.log10(num));
    const multiple = Math.pow(10, log10);

    return Math.ceil(num / multiple) * multiple;
}

const createDashboardOptions = (type: DashboardType) => {
    const url = getUrl(type);
    const getActive = createGetActiveThunk(type, url);

    return {
        actions: {
            getActive
        },
        options: {
            name: type + 'Dashboard',
            initialState: initInitialState(type),
            extraReducers: (builder) => {
                builder
                    .addCase(getActive.pending, (state, action) => {
                        state.isLoading = true;
                    })
                    .addCase(getActive.fulfilled, (state, action) => {
                        const records: Records = action.payload;
                        state.categories = records.dates;
                        state.series = records.series;
                        state.yaxisMax = roundToMultiple(records.max);

                        filterSeries(state);
                    })
            },
            reducers: {
                selectPlatform: (state, action: PayloadAction<Platform>) => {
                    const platform = action.payload;
                    state.selectedPlatforms.push(platform);

                    filterSeries(state);
                },
                excludePlatform: (state, action: PayloadAction<Platform>) => {
                    const platform = action.payload;
                    const index = state.selectedPlatforms.indexOf(platform);
                    if (index > -1) state.selectedPlatforms.splice(index, 1);

                    filterSeries(state);
                },
                selectCountry: (state, action: PayloadAction<string>) => {
                    const country = action.payload;
                    state.selectedCountries.push(country);

                    filterSeries(state);
                },
                excludeCountry: (state, action: PayloadAction<string>) => {
                    const country = action.payload;
                    const index = state.selectedCountries.indexOf(country);
                    if (index > -1) state.selectedCountries.splice(index, 1);

                    filterSeries(state);
                }
            }
        }
    }
}

const accountDashboardOptions = createDashboardOptions('Account');
const accountDashboardSlice = createSlice(accountDashboardOptions.options);

const memberDashboardOptions = createDashboardOptions('Member');
const memberDashboardSlice = createSlice(memberDashboardOptions.options);

export const getActiveAction = (type: DashboardType) => {
    switch (type) {
        case "Account":
            return accountDashboardOptions.actions.getActive;
        case "Member":
            return memberDashboardOptions.actions.getActive;
    }
}

export const getExtraActions = (type: DashboardType) => {
    switch (type) {
        case "Account":
            return accountDashboardOptions.actions;
        case "Member":
            return memberDashboardOptions.actions;
    }
}

export const getSliceActions = (type: DashboardType) => {
    switch (type) {
        case "Account":
            return accountDashboardSlice.actions;
        case "Member":
            return memberDashboardSlice.actions;
    }
}

export const accountDashboardReducer = accountDashboardSlice.reducer;
export const memberDashboardReducer = memberDashboardSlice.reducer;